import React, { useEffect, useState } from 'react'
import { Modal, Button, Form, Row, Col } from 'react-bootstrap'
import Select from 'react-select'
import { useQuery } from '@apollo/client'
import { MultiSelectWithActions } from 'app/tfi/components/MultiSelectWithActions'
import { DateInput } from 'app/tfi/components/DateInput'
import { GET_ALL_POLICIES_STATUSES, GET_ALL_POLICIES_TYPES } from '../../../gql/queries/policiesQueries'
import { GET_ALL_USERS_FILTER, GET_ASSIGNMENT_USERS_LIST } from '../../../gql/queries/usersQuery'
import { GET_ALL_LEAD_SOURCES } from '../../../gql/queries/leadSourceQuery'
import { parseId } from '../../../helpers'
import { useIntl, FormattedMessage } from 'react-intl'
import {
  RECIPIENT_MESSAGES as M,
  POLICY_FILTER_MESSAGES as P,
  translatedCatalogs,
} from '../recipientMessages'
import { RecipientFilters } from '../recipientsTypes'

function parseBool(v: string | undefined): boolean | undefined {
  if (v === 'true') return true
  if (v === 'false') return false
  return undefined
}

interface StateOption { id: string; name: string; code: string }
interface CarrierOption { id: string; name: string }

interface Props {
  show: boolean
  onHide: () => void
  filters: RecipientFilters
  onApply: (filters: Partial<RecipientFilters>) => void
  states?: StateOption[]
  carriers?: CarrierOption[]
}

export const RecipientAdvancedFiltersModal: React.FC<Props> = ({
  show,
  onHide,
  filters,
  onApply,
  states = [],
  carriers = [],
}) => {
  const intl = useIntl()
  const catalogs = translatedCatalogs(intl)
  const yesNoOptions = [
    { value: 'true', label: intl.formatMessage(P.yes) },
    { value: 'false', label: intl.formatMessage(P.no) },
  ]
  /** El booleano del filtro → la opción Sí/No ya traducida. */
  const boolVal = (val: boolean | undefined) =>
    val === undefined ? null : yesNoOptions[val ? 0 : 1]

  const jurisdictionOptions = [
    { value: 'FEDERAL', label: intl.formatMessage(P.federal) },
    { value: 'STATE', label: intl.formatMessage(P.stateJurisdiction) },
  ]
  const unassignedOptions = [
    { value: 'true', label: intl.formatMessage(P.onlyUnassigned) },
    { value: 'false', label: intl.formatMessage(P.onlyAssigned) },
  ]
  const memberIdOptions = [
    { value: 'true', label: intl.formatMessage(P.withMemberId) },
    { value: 'false', label: intl.formatMessage(P.withoutMemberId) },
  ]
  const commissionPayableOptions = [
    { value: 'true', label: intl.formatMessage(P.consideredCommission) },
    { value: 'false', label: intl.formatMessage(P.notConsideredCommission) },
  ]
  const anyPlaceholder = intl.formatMessage(P.placeholderAny)
  const allPoliciesPlaceholder = intl.formatMessage(P.allPolicies)

  const [pending, setPending] = useState<RecipientFilters>(filters)

  useEffect(() => {
    if (show) setPending(filters)
  }, [show]) // eslint-disable-line react-hooks/exhaustive-deps

  const { data: policyStatusesData } = useQuery(GET_ALL_POLICIES_STATUSES, { skip: !show })
  const { data: policyTypesData } = useQuery(GET_ALL_POLICIES_TYPES, {
    skip: !show,
    variables: { first: 100 },
  })
  const { data: agentsData } = useQuery(GET_ALL_USERS_FILTER, {
    skip: !show,
    variables: { requireNpn: true },
  })
  const { data: assignedToData } = useQuery(GET_ASSIGNMENT_USERS_LIST, {
    skip: !show,
    variables: { assignmentCandidates: true },
  })
  const { data: leadSourcesData } = useQuery(GET_ALL_LEAD_SOURCES, {
    skip: !show,
    variables: { first: 200 },
  })

  // Policy statuses grouped
  const rawStatuses: { code: string; title: string; category: string }[] =
    policyStatusesData?.allPolicyStatuses?.edges?.map((e: any) => e.node) ?? []
  const grouped: Record<string, { value: string; label: string }[]> = {}
  rawStatuses.forEach((s) => {
    const cat = s.category || 'OTHER'
    if (!grouped[cat]) grouped[cat] = []
    grouped[cat].push({ value: s.code, label: s.title })
  })
  const policyStatusGrouped = Object.keys(grouped).map((cat) => ({
    label: cat.replace(/_/g, ' '),
    options: grouped[cat],
  }))
  const allStatusFlat = policyStatusGrouped.flatMap((g) => g.options)

  const policyTypeOptions: { value: string; label: string }[] =
    policyTypesData?.allPoliciesType?.edges?.map((e: any) => ({
      value: String(parseId(e.node.id)),
      label: e.node.typePolicy,
    })) ?? []

  const agentsOptions: { value: string; label: string }[] =
    agentsData?.allUsers?.edges?.map((e: any) => ({
      value: String(parseId(e.node.id)),
      label: `${e.node.firstName} ${e.node.lastName} - ${e.node.email}${e.node.npn ? ` (${e.node.npn})` : ''}`,
    })) ?? []

  const assignedToOptions: { value: string; label: string }[] =
    assignedToData?.allUsersList?.map((u: any) => ({
      value: u.id,
      label: `${u.firstName} ${u.lastName} - ${u.email}${u.npn ? ` (${u.npn})` : ''}`,
    })) ?? []

  const leadSourceOptions: { value: string; label: string }[] =
    leadSourcesData?.allLeadSources?.edges?.map((e: any) => ({
      value: String(parseId(e.node.id)),
      label: e.node.name,
    })) ?? []

  const stateOptions = states.map((s) => ({ value: s.code, label: `${s.name} (${s.code})` }))
  const carrierOptions = carriers.map((c) => ({ value: c.id, label: c.name }))

  const set = (patch: Partial<RecipientFilters>) => setPending((f) => ({ ...f, ...patch }))
  const multi = (field: keyof RecipientFilters, sel: any) =>
    set({ [field]: sel ? sel.map((o: any) => o.value) : [] } as any)

  const handleDateChange = (name: string, value: string) =>
    setPending((f) => ({ ...f, [name]: value }))

  const policyRelationshipValue =
    pending.withoutPolicy === true
      ? 'no_policy'
      : pending.isPolicyHolder === true
        ? 'holder'
        : pending.isPolicyHolder === false && pending.withoutPolicy === false
          ? 'family'
          : ''

  const handleApply = () => {
    onApply(pending)
    onHide()
  }

  const handleClear = () =>
    setPending({
      ...filters,
      state: [],
      carrier: [],
      policyStatus: [],
      policyType: [],
      isPolicyHolder: undefined,
      withoutPolicy: undefined,
      unassigned: undefined,
      user: [],
      assignedTo: [],
      stateJurisdictionType: '',
      zipCode: '',
      immigrationStatus: [],
      documentStatus: [],
      paymentStatus: [],
      leadSource: [],
      metalLevel: '',
      holderGender: '',
      hasMemberId: undefined,
      commissionPayable: undefined,
      commissionPaymentStatus: '',
      ownsCar: undefined,
      ownsHome: undefined,
      hasAutoPolicy: undefined,
      hasHomePolicy: undefined,
      policyCreatedFrom: '',
      policyCreatedTo: '',
      submissionDateFrom: '',
      submissionDateTo: '',
      carrierStatusGeneric: [],
      carrierLastAsOfDateAfter: '',
      carrierLastAsOfDateBefore: '',
      carrierIsAgeIn: undefined,
      carrierIsDelinquent: undefined,
      carrierIsUnpaidPending: undefined,
      carrierIsUnpaidPastDue: undefined,
    })

  return (
    <Modal show={show} onHide={onHide} size='xl' centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title><FormattedMessage {...P.title} /></Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='d-flex justify-content-end gap-2 mb-4'>
          <Button variant='light' className='px-3' onClick={handleClear}>
            <FormattedMessage {...P.clear} />
          </Button>
          <Button variant='primary' className='px-3' onClick={handleApply}>
            <i className='bi bi-search me-1' /><FormattedMessage {...P.applyFilters} />
          </Button>
        </div>

        <Form>
          {/* ── Agent / Assignment ── */}
          <Row className='mb-3 g-3 row-cols-1 row-cols-md-3'>
            <Col>
              <Form.Label><FormattedMessage {...P.agent} /></Form.Label>
              <MultiSelectWithActions
                value={agentsOptions.filter((o) => pending.user.includes(o.value))}
                options={agentsOptions}
                onChange={(sel: any) => multi('user', sel)}
              />
            </Col>
            <Col>
              <Form.Label><FormattedMessage {...P.assignedTo} /></Form.Label>
              <MultiSelectWithActions
                value={assignedToOptions.filter((o) => pending.assignedTo.includes(o.value))}
                options={assignedToOptions}
                onChange={(sel: any) => multi('assignedTo', sel)}
              />
            </Col>
            <Col>
              <Form.Label><FormattedMessage {...P.carrier} /></Form.Label>
              <MultiSelectWithActions
                value={carrierOptions.filter((o) => pending.carrier.includes(String(o.value)))}
                options={carrierOptions}
                onChange={(sel: any) => multi('carrier', sel)}
              />
            </Col>
            <Col>
              <Form.Label><FormattedMessage {...P.state} /></Form.Label>
              <MultiSelectWithActions
                value={stateOptions.filter((o) => pending.state.includes(o.value))}
                options={stateOptions}
                onChange={(sel: any) => multi('state', sel)}
              />
            </Col>
            <Col>
              <Form.Label><FormattedMessage {...P.stateJurisdictionType} /></Form.Label>
              <Select
                value={
                  jurisdictionOptions.find((o) => o.value === pending.stateJurisdictionType) ?? null
                }
                options={jurisdictionOptions}
                onChange={(sel: any) => set({ stateJurisdictionType: sel?.value ?? '' })}
                isClearable
                placeholder={intl.formatMessage(P.placeholderSelectJurisdiction)}
              />
            </Col>
            <Col>
              <Form.Label><FormattedMessage {...P.zipCode} /></Form.Label>
              <Form.Control
                type='number'
                value={pending.zipCode}
                onChange={(e) => set({ zipCode: e.target.value })}
              />
            </Col>
          </Row>

          {/* ── Policy ── */}
          <Row className='mb-3 g-3 row-cols-1 row-cols-md-3'>
            <Col>
              <Form.Label><FormattedMessage {...P.policyStatus} /></Form.Label>
              <MultiSelectWithActions
                value={allStatusFlat.filter((o) => pending.policyStatus.includes(o.value))}
                options={policyStatusGrouped}
                onChange={(sel: any) => multi('policyStatus', sel)}
              />
            </Col>
            <Col>
              <Form.Label><FormattedMessage {...P.policyTypes} /></Form.Label>
              <MultiSelectWithActions
                value={policyTypeOptions.filter((o) => pending.policyType.includes(String(o.value)))}
                options={policyTypeOptions}
                onChange={(sel: any) => multi('policyType', sel)}
              />
            </Col>
            <Col>
              <Form.Label><FormattedMessage {...M.policyRelationship} /></Form.Label>
              <Form.Select
                value={policyRelationshipValue}
                onChange={(e) =>
                  set({
                    isPolicyHolder:
                      e.target.value === 'holder' ? true : e.target.value === 'family' ? false : undefined,
                    withoutPolicy:
                      e.target.value === 'no_policy'
                        ? true
                        : e.target.value === 'holder' || e.target.value === 'family'
                          ? false
                          : undefined,
                  })
                }
              >
                <option value=''>{intl.formatMessage(M.optAllContacts)}</option>
                <option value='holder'>{intl.formatMessage(M.optPolicyHolders)}</option>
                <option value='family'>{intl.formatMessage(M.optFamilyMembers)}</option>
                <option value='no_policy'>{intl.formatMessage(M.optNoPolicy)}</option>
              </Form.Select>
            </Col>
            <Col>
              <Form.Label><FormattedMessage {...P.unassignedPolicies} /></Form.Label>
              <Select
                value={
                  pending.unassigned === undefined
                    ? null
                    : unassignedOptions[pending.unassigned ? 0 : 1]
                }
                options={unassignedOptions}
                onChange={(sel: any) => set({ unassigned: parseBool(sel?.value) })}
                isClearable
                placeholder={allPoliciesPlaceholder}
              />
            </Col>
            <Col>
              <Form.Label><FormattedMessage {...P.source} /></Form.Label>
              <MultiSelectWithActions
                value={leadSourceOptions.filter((o) => pending.leadSource.includes(o.value))}
                options={leadSourceOptions}
                onChange={(sel: any) => multi('leadSource', sel)}
              />
            </Col>
            <Col>
              <Form.Label><FormattedMessage {...P.metalLevel} /></Form.Label>
              <Select
                value={catalogs.metal.find((o) => o.value === pending.metalLevel) ?? null}
                options={catalogs.metal}
                onChange={(sel: any) => set({ metalLevel: sel?.value ?? '' })}
                isClearable
                placeholder={intl.formatMessage(P.placeholderSelectMetalLevel)}
              />
            </Col>
          </Row>

          {/* ── Personal attributes ── */}
          <Row className='mb-3 g-3 row-cols-1 row-cols-md-3'>
            <Col>
              <Form.Label><FormattedMessage {...P.immigrationStatus} /></Form.Label>
              <MultiSelectWithActions
                value={catalogs.immigration.filter((o) => pending.immigrationStatus.includes(o.value))}
                options={catalogs.immigration}
                onChange={(sel: any) => multi('immigrationStatus', sel)}
              />
            </Col>
            <Col>
              <Form.Label><FormattedMessage {...P.documentStatus} /></Form.Label>
              <MultiSelectWithActions
                value={catalogs.document.filter((o) => pending.documentStatus.includes(o.value))}
                options={catalogs.document}
                onChange={(sel: any) => multi('documentStatus', sel)}
              />
            </Col>
            <Col>
              <Form.Label><FormattedMessage {...P.paymentStatus} /></Form.Label>
              <MultiSelectWithActions
                value={catalogs.payment.filter((o) => pending.paymentStatus.includes(o.value))}
                options={catalogs.payment}
                onChange={(sel: any) => multi('paymentStatus', sel)}
              />
            </Col>
            <Col>
              <Form.Label><FormattedMessage {...P.genderHolder} /></Form.Label>
              <Select
                value={catalogs.gender.find((o) => o.value === pending.holderGender) ?? null}
                options={catalogs.gender}
                onChange={(sel: any) => set({ holderGender: sel?.value ?? '' })}
                isClearable
                placeholder={intl.formatMessage(P.selectOption)}
              />
            </Col>
            <Col>
              <Form.Label><FormattedMessage {...P.memberIdStatus} /></Form.Label>
              <Select
                value={
                  pending.hasMemberId === undefined
                    ? null
                    : memberIdOptions[pending.hasMemberId ? 0 : 1]
                }
                options={memberIdOptions}
                onChange={(sel: any) => set({ hasMemberId: parseBool(sel?.value) })}
                isClearable
                placeholder={allPoliciesPlaceholder}
              />
            </Col>
          </Row>

          {/* ── Commission ── */}
          <Row className='mb-3 g-3 row-cols-1 row-cols-md-3'>
            <Col>
              <Form.Label><FormattedMessage {...P.commissionReportStatus} /></Form.Label>
              <Select
                value={
                  pending.commissionPayable === undefined
                    ? null
                    : commissionPayableOptions[pending.commissionPayable ? 0 : 1]
                }
                options={commissionPayableOptions}
                onChange={(sel: any) => set({ commissionPayable: parseBool(sel?.value) })}
                isClearable
                placeholder={allPoliciesPlaceholder}
              />
            </Col>
            <Col>
              <Form.Label><FormattedMessage {...P.commissionPaymentStatus} /></Form.Label>
              <Select
                value={
                  catalogs.commissionStatus.find((o) => o.value === pending.commissionPaymentStatus) ?? null
                }
                options={catalogs.commissionStatus}
                onChange={(sel: any) => set({ commissionPaymentStatus: sel?.value ?? '' })}
                isClearable
                placeholder={allPoliciesPlaceholder}
              />
            </Col>
          </Row>

          {/* ── Property / extras ── */}
          <Row className='mb-3 g-3 row-cols-1 row-cols-md-4'>
            <Col>
              <Form.Label><FormattedMessage {...P.ownsCar} /></Form.Label>
              <Select
                value={boolVal(pending.ownsCar)}
                options={yesNoOptions}
                onChange={(sel: any) => set({ ownsCar: parseBool(sel?.value) })}
                isClearable
                placeholder={anyPlaceholder}
              />
            </Col>
            <Col>
              <Form.Label><FormattedMessage {...P.ownsHome} /></Form.Label>
              <Select
                value={boolVal(pending.ownsHome)}
                options={yesNoOptions}
                onChange={(sel: any) => set({ ownsHome: parseBool(sel?.value) })}
                isClearable
                placeholder={anyPlaceholder}
              />
            </Col>
            <Col>
              <Form.Label><FormattedMessage {...P.hasAutoPolicy} /></Form.Label>
              <Select
                value={boolVal(pending.hasAutoPolicy)}
                options={yesNoOptions}
                onChange={(sel: any) => set({ hasAutoPolicy: parseBool(sel?.value) })}
                isClearable
                placeholder={anyPlaceholder}
              />
            </Col>
            <Col>
              <Form.Label><FormattedMessage {...P.hasHomePolicy} /></Form.Label>
              <Select
                value={boolVal(pending.hasHomePolicy)}
                options={yesNoOptions}
                onChange={(sel: any) => set({ hasHomePolicy: parseBool(sel?.value) })}
                isClearable
                placeholder={anyPlaceholder}
              />
            </Col>
          </Row>

          <hr />

          {/* ── Date Ranges ── */}
          <h6 className='text-muted mb-3'><FormattedMessage {...M.sectionDateRanges} /></h6>
          <Row className='g-3 row-cols-1 row-cols-md-2 row-cols-xl-4 mb-3'>
            <Col>
              <DateInput
                id='policyCreatedFrom'
                name='policyCreatedFrom'
                label={intl.formatMessage(M.policyCreatedFrom)}
                value={pending.policyCreatedFrom}
                onChange={handleDateChange}
              />
            </Col>
            <Col>
              <DateInput
                id='policyCreatedTo'
                name='policyCreatedTo'
                label={intl.formatMessage(M.policyCreatedTo)}
                value={pending.policyCreatedTo}
                onChange={handleDateChange}
              />
            </Col>
            <Col>
              <DateInput
                id='submissionDateFrom'
                name='submissionDateFrom'
                label={intl.formatMessage(M.submissionDateFrom)}
                value={pending.submissionDateFrom}
                onChange={handleDateChange}
              />
            </Col>
            <Col>
              <DateInput
                id='submissionDateTo'
                name='submissionDateTo'
                label={intl.formatMessage(M.submissionDateTo)}
                value={pending.submissionDateTo}
                onChange={handleDateChange}
              />
            </Col>
          </Row>

          <hr />

          {/* ── Carrier Import Filters ── */}
          <h5><FormattedMessage {...P.sectionCarrierImport} /></h5>
          <Row className='mb-3 g-2 align-items-end'>
            <Col xs={12} md={6} xl={3}>
              <Form.Label><FormattedMessage {...P.carrierStatus} /></Form.Label>
              <Select
                value={catalogs.carrierStatus.filter((o) => pending.carrierStatusGeneric.includes(o.value))}
                options={catalogs.carrierStatus}
                onChange={(sel: any) =>
                  set({ carrierStatusGeneric: sel ? sel.map((o: any) => o.value) : [] })
                }
                isMulti
                isClearable
                placeholder={intl.formatMessage(P.placeholderSelectCarrierStatus)}
              />
            </Col>
            <Col xs={12} md={6} xl={2}>
              <DateInput
                id='carrierLastAsOfDateAfter'
                name='carrierLastAsOfDateAfter'
                label={intl.formatMessage(P.carrierAsOfFrom)}
                value={pending.carrierLastAsOfDateAfter}
                onChange={handleDateChange}
              />
            </Col>
            <Col xs={12} md={6} xl={2}>
              <DateInput
                id='carrierLastAsOfDateBefore'
                name='carrierLastAsOfDateBefore'
                label={intl.formatMessage(P.carrierAsOfTo)}
                value={pending.carrierLastAsOfDateBefore}
                onChange={handleDateChange}
              />
            </Col>
            <Col xs={12} md={6} xl={1}>
              <Form.Label className='small text-nowrap'><FormattedMessage {...P.ageIn} /></Form.Label>
              <Select
                value={boolVal(pending.carrierIsAgeIn)}
                options={yesNoOptions}
                onChange={(sel: any) => set({ carrierIsAgeIn: parseBool(sel?.value) })}
                isClearable
                placeholder={anyPlaceholder}
              />
            </Col>
            <Col xs={12} md={6} xl={1}>
              <Form.Label className='small text-nowrap'><FormattedMessage {...P.delinquent} /></Form.Label>
              <Select
                value={boolVal(pending.carrierIsDelinquent)}
                options={yesNoOptions}
                onChange={(sel: any) => set({ carrierIsDelinquent: parseBool(sel?.value) })}
                isClearable
                placeholder={anyPlaceholder}
              />
            </Col>
            <Col xs={12} md={6} xl={2}>
              <Form.Label className='small text-nowrap'><FormattedMessage {...P.unpaidPending} /></Form.Label>
              <Select
                value={boolVal(pending.carrierIsUnpaidPending)}
                options={yesNoOptions}
                onChange={(sel: any) => set({ carrierIsUnpaidPending: parseBool(sel?.value) })}
                isClearable
                placeholder={anyPlaceholder}
              />
            </Col>
            <Col xs={12} md={6} xl={2}>
              <Form.Label className='small text-nowrap'><FormattedMessage {...P.unpaidPastDue} /></Form.Label>
              <Select
                value={boolVal(pending.carrierIsUnpaidPastDue)}
                options={yesNoOptions}
                onChange={(sel: any) => set({ carrierIsUnpaidPastDue: parseBool(sel?.value) })}
                isClearable
                placeholder={anyPlaceholder}
              />
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant='light' className='px-4' onClick={onHide}>
          <FormattedMessage {...M.cancel} />
        </Button>
        <Button variant='primary' className='px-4' onClick={handleApply}>
          <i className='bi bi-search me-1' /><FormattedMessage {...P.applyFilters} />
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
